import { useTranslation } from "react-i18next";
import { Button, Input } from "../ui";
import type { QuoteLine, QuoteOverrides } from "../../features/quotes/types";
import { formatMoney } from "./QuoteTotals";

interface QuoteLinesTableProps {
  /** Líneas ya recomputadas (servidor + overrides locales). */
  lines: QuoteLine[];
  /** Líneas originales del servidor (para "restaurar"). */
  baseLines: QuoteLine[];
  overrides: QuoteOverrides;
  onChange: (next: QuoteOverrides) => void;
  currency: string;
  readOnly?: boolean;
}

/** Desglose editable: cada concepto admite cambiar el importe, quitarse o restaurarse; se pueden agregar conceptos propios. */
export function QuoteLinesTable({ lines, baseLines, overrides, onChange, currency, readOnly }: QuoteLinesTableProps) {
  const { t } = useTranslation("finanzasOperativas");
  const removed = new Set(overrides.remove ?? []);
  const removedLines = baseLines.filter((l) => removed.has(l.key));

  const setLineTotal = (key: string, total: number) =>
    onChange({ ...overrides, lines: { ...(overrides.lines ?? {}), [key]: { ...(overrides.lines?.[key] ?? {}), total } } });
  const restore = (key: string) => {
    const nextLines = { ...(overrides.lines ?? {}) };
    delete nextLines[key];
    onChange({ ...overrides, lines: nextLines, remove: (overrides.remove ?? []).filter((k) => k !== key) });
  };
  const removeLine = (key: string) => onChange({ ...overrides, remove: [...(overrides.remove ?? []).filter((k) => k !== key), key] });

  const customLines = overrides.custom_lines ?? [];
  const setCustom = (i: number, patch: Partial<{ label: string; qty: number; unit_cost: number }>) => {
    const next = customLines.map((c, j) => (j === i ? { ...c, ...patch } : c));
    onChange({ ...overrides, custom_lines: next });
  };
  const removeCustom = (i: number) => onChange({ ...overrides, custom_lines: customLines.filter((_, j) => j !== i) });
  const addCustom = () => onChange({ ...overrides, custom_lines: [...customLines, { label: "", qty: 1, unit: "viaje", unit_cost: 0 }] });

  const customIndex = (key: string) => Number(key.replace("custom_", "")) - 1;

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-line">
        <table className="w-full text-xs">
          <thead className="bg-subtle text-content-secondary">
            <tr>
              <th className="px-3 py-2 text-left font-medium">{t("quotes.calculation.concept")}</th>
              <th className="px-3 py-2 text-right font-medium">{t("quotes.calculation.qty")}</th>
              <th className="hidden px-3 py-2 text-right font-medium sm:table-cell">{t("quotes.calculation.unitCost")}</th>
              <th className="px-3 py-2 text-right font-medium">{t("quotes.calculation.total")}</th>
              {!readOnly && <th className="w-20 px-2 py-2" />}
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const isCustom = Boolean(l.custom);
              const ci = isCustom ? customIndex(l.key) : -1;
              return (
                <tr key={l.key} className="border-t border-line">
                  <td className="px-3 py-1.5 text-content">
                    {isCustom && !readOnly ? (
                      <Input size="xs" value={customLines[ci]?.label ?? ""} placeholder={t("quotes.calculation.concept")} onChange={(e) => setCustom(ci, { label: e.target.value })} />
                    ) : (
                      <span className={l.overridden ? "font-medium" : ""}>{l.label}</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-content-secondary">
                    {isCustom && !readOnly ? (
                      <Input size="xs" type="number" min={0} step="0.01" className="text-right" containerClassName="ml-auto w-20" value={customLines[ci]?.qty ?? 1} onChange={(e) => setCustom(ci, { qty: Number(e.target.value) })} />
                    ) : (
                      `${l.qty.toLocaleString("es-MX")} ${l.unit}`
                    )}
                  </td>
                  <td className="hidden px-3 py-1.5 text-right tabular-nums text-content-secondary sm:table-cell">
                    {isCustom && !readOnly ? (
                      <Input size="xs" type="number" min={0} step="0.01" className="text-right" containerClassName="ml-auto w-28" value={customLines[ci]?.unit_cost ?? 0} onChange={(e) => setCustom(ci, { unit_cost: Number(e.target.value) })} />
                    ) : (
                      formatMoney(l.unit_cost, currency)
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {readOnly || isCustom ? (
                      <span className="font-medium text-content">{formatMoney(l.total, currency)}</span>
                    ) : (
                      <Input size="xs" type="number" min={0} step="0.01" className="text-right font-medium" containerClassName="ml-auto w-32" value={l.total} onChange={(e) => setLineTotal(l.key, Number(e.target.value))} />
                    )}
                  </td>
                  {!readOnly && (
                    <td className="px-2 py-1.5">
                      <div className="flex justify-end gap-1">
                        {l.overridden && !isCustom && (
                          <Button variant="ghost" size="xs" iconOnly title={t("quotes.calculation.restore")} icon={<i className="ri-arrow-go-back-line" />} onClick={() => restore(l.key)} />
                        )}
                        <Button variant="ghost" size="xs" iconOnly title={t("quotes.calculation.remove")} icon={<i className="ri-close-line" />} onClick={() => (isCustom ? removeCustom(ci) : removeLine(l.key))} />
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {removedLines.length > 0 && !readOnly && (
              <tr className="border-t border-line bg-subtle/50">
                <td colSpan={5} className="px-3 py-1.5 text-content-faint">
                  <span className="mr-2">{t("quotes.calculation.remove")}:</span>
                  {removedLines.map((l) => (
                    <Button key={l.key} variant="link" size="xs" onClick={() => restore(l.key)}>
                      {l.label} ↺
                    </Button>
                  ))}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <Button variant="dashed" size="sm" fullWidth icon={<i className="ri-add-line" />} onClick={addCustom}>
          {t("quotes.calculation.addLine")}
        </Button>
      )}
    </div>
  );
}
